/**
 * AI Recruit - AI Interviewer control panel (recruiter room, "ai" mode only).
 *
 * Shows where the autonomous interview stands (phase) and the flag payload
 * (start_interview / move_forward / end_interview) of every AI response,
 * and lets the recruiter start the interview or move to the next question.
 * Ending uses the room's own "End Interview" button (below Leave). A
 * command is applied server-side immediately and reaches the candidate's
 * room on its next events poll (~3s).
 */
import { useCallback, useEffect, useState } from 'react'
import { toast } from 'react-hot-toast'
import {
  controlAiInterviewer, getAiInterviewerState,
  type AiInterviewerControlAction, type AiInterviewerState,
} from '@/services/liveInterviewApi'

const POLL_MS = 3000

const PHASE_LABELS: Record<string, string> = {
  greeting: 'Waiting for candidate',
  preamble: 'Warm-up / self-intro',
  ready_check: 'Waiting for "ready"',
  in_interview: 'Interview in progress',
  closing: 'Wrapping up',
  done: 'Completed',
}

const sourceLabel = (source: string) =>
  source === 'ai' ? 'AI' : source === 'candidate_button' ? 'Candidate button' : source.startsWith('recruiter') ? 'Recruiter' : source

const ACTIONS: { action: AiInterviewerControlAction; label: string; confirm?: string; className: string }[] = [
  { action: 'start', label: 'Start Interview', className: 'bg-blue-600 hover:bg-blue-700 text-white' },
  { action: 'move_forward', label: 'Next Question', className: 'bg-white hover:bg-gray-50 text-gray-700 border border-gray-300' },
]

export default function AiInterviewerControlPanel({ sessionId }: { sessionId: string }) {
  const [state, setState] = useState<AiInterviewerState | null>(null)
  const [busy, setBusy] = useState<AiInterviewerControlAction | null>(null)
  const [collapsed, setCollapsed] = useState(false)

  const refresh = useCallback(() => {
    getAiInterviewerState(sessionId).then(setState).catch(() => { /* next tick retries */ })
  }, [sessionId])

  useEffect(() => {
    refresh()
    const interval = setInterval(refresh, POLL_MS)
    return () => clearInterval(interval)
  }, [refresh])

  const run = async (action: AiInterviewerControlAction, confirmText?: string) => {
    if (busy || (confirmText && !window.confirm(confirmText))) return
    setBusy(action)
    try {
      setState(await controlAiInterviewer(sessionId, action))
      toast.success('Sent to the candidate’s interview room')
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || 'Could not apply that action.')
    } finally {
      setBusy(null)
    }
  }

  const ended = state?.status === 'completed' || state?.status === 'aborted'

  return (
    <div className="fixed bottom-4 right-4 z-[60] w-80 max-w-[calc(100vw-2rem)] bg-white border border-gray-200 rounded-xl shadow-lg text-sm">
      <button
        onClick={() => setCollapsed((c) => !c)}
        className="w-full flex items-center justify-between px-4 py-2.5 border-b border-gray-100"
      >
        <span className="font-semibold text-gray-900">AI Interviewer control</span>
        <span className="text-xs text-gray-500">
          {state ? PHASE_LABELS[state.phase] || state.phase : 'Loading…'} {collapsed ? '▴' : '▾'}
        </span>
      </button>
      {!collapsed && (
        <div className="p-3 space-y-3">
          <div className="flex flex-wrap gap-2">
            {ACTIONS.map(({ action, label, confirm, className }) => {
              const enabled = !ended && !!state?.available_actions.includes(action) && !busy
              return (
                <button
                  key={action}
                  disabled={!enabled}
                  onClick={() => run(action, confirm)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${className}`}
                >
                  {busy === action ? 'Sending…' : label}
                </button>
              )
            })}
          </div>
          {!!state?.pending_events && (
            <p className="text-xs text-amber-600">Waiting for the candidate's room to apply {state.pending_events} command(s)...</p>
          )}
          <div>
            <p className="text-xs font-medium text-gray-500 mb-1">AI response flags</p>
            <ul className="max-h-48 overflow-y-auto space-y-1.5">
              {(state?.decisions || []).length === 0 && <li className="text-xs text-gray-400">No decisions yet.</li>}
              {(state?.decisions || []).map((d, i) => {
                const raised = Object.entries(d.flags || {}).filter(([, v]) => v).map(([k]) => k)
                return (
                  <li key={`${d.at}-${i}`} className="text-xs border border-gray-100 rounded-md px-2 py-1.5">
                    <div className="flex items-center justify-between gap-2">
                      <span className={`font-semibold ${raised.length ? 'text-green-700' : 'text-gray-500'}`}>
                        {raised.length ? raised.map((f) => `${f}: true`).join(', ') : 'no action (all false)'}
                      </span>
                      <span className="text-gray-400 shrink-0">{sourceLabel(d.source)}</span>
                    </div>
                    {d.candidate_text && <p className="text-gray-600 truncate" title={d.candidate_text}>"{d.candidate_text}"</p>}
                  </li>
                )
              })}
            </ul>
          </div>
        </div>
      )}
    </div>
  )
}
